/**
 * Moderation playground composable
 * Manages content moderation state and API calls
 */

import { ref, computed } from 'vue';
import { useNotification } from '@asabdev/asab-ui';
import { useApiError } from '@/features/auth/composables/useApiError';
import { useAuthStore } from '@/stores/auth';
import { useSessionStorage } from './useSessionStorage';
import { createOpenAIClient } from '../lib/openai-client';
import type { ModerationResult } from '../types/session';

export function useModerationPlayground() {
  const authStore = useAuthStore();
  const { clearError, safeApiCall } = useApiError();
  const { showSuccess, showError } = useNotification();
  const { session, loadSession, saveSession, createEmptySession } = useSessionStorage();

  if (!session.value) {
    const loaded = loadSession();
    if (!loaded) {
      session.value = createEmptySession();
    }
  }

  const inputText = ref<string>(session.value?.moderation.inputText || '');
  const selectedModel = ref<string>(session.value?.moderation.selectedModel || '');
  const results = ref<ModerationResult | null>(session.value?.moderation.results || null);
  const isLoading = ref(false);

  const moderateContent = async (text: string): Promise<void> => {
    if (!authStore.token || !selectedModel.value) return;

    isLoading.value = true;
    clearError();

    const result = await safeApiCall(
      async () => {
        const client = createOpenAIClient({
          token: authStore.token!,
          isApiKey: false,
        });

        const response = await client.moderations.create({
          model: selectedModel.value,
          input: text,
        });

        const moderationResult = response.results[0];
        results.value = {
          flagged: moderationResult.flagged,
          categories: moderationResult.categories as unknown as Record<string, boolean>,
          categoryScores: moderationResult.category_scores as unknown as Record<string, number>,
        };
        return true;
      },
      {
        context: 'Content Moderation',
        retryOptions: { maxRetries: 2, initialDelay: 1000 },
      }
    );

    isLoading.value = false;

    if (result) {
      showSuccess('Content moderated successfully');
      updateSession();
    } else {
      showError('Failed to moderate content');
    }
  };

  const setModel = (modelId: string): void => {
    selectedModel.value = modelId;
    updateSession();
  };

  const setInputText = (text: string): void => {
    inputText.value = text;
    updateSession();
  };

  const updateSession = (): void => {
    if (session.value) {
      session.value.moderation = {
        inputText: inputText.value,
        selectedModel: selectedModel.value,
        results: results.value,
      };
      session.value.timestamp = new Date();
      saveSession(session.value);
    }
  };

  return {
    inputText: computed(() => inputText.value),
    selectedModel: computed(() => selectedModel.value),
    results: computed(() => results.value),
    isLoading: computed(() => isLoading.value),
    moderateContent,
    setModel,
    setInputText,
  };
}
